import { cx } from '@emotion/css';
import { BlockBoundary, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useCallback, useContext } from 'react';

import { SearchBar } from '../SearchBar/SearchBar';
import { JobFilterField } from './constants';
import { JobMultiSelectFilters } from './JobMultiSelectFilters';
import { JobsTable } from './JobsTable';
import {
  boundaryCss,
  filterContainerCss,
  filtersContainerCss,
  jobFiltersBlockCss,
  jobSearchBarBlockCss,
  searchHeaderCss,
  searchSectionCss,
} from './styles';
import type { DropdownMenu, JobFilters, JobPostingsProps } from './types';

const Filters: FC<DropdownMenu> = ({ filterNames, filterValues, filters, handleFilterChange }) => {
  const { formatMessage } = useContext(MessageContext);
  const filterTitleMap: Record<JobFilters, string> = {
    role: formatMessage({ id: 'roleFilterTitle', defaultMessage: 'All roles' }),
    location: formatMessage({ id: 'locationFilterTitle', defaultMessage: 'All locations' }),
    type: formatMessage({ id: 'typeFilterTitle', defaultMessage: 'All types' }),
  };

  return (
    <div className={filtersContainerCss}>
      {filterNames.map((filterName: JobFilters) => (
        <JobMultiSelectFilters
          key={filterName}
          items={
            filterValues?.[filterName]?.map((value: string) => ({
              id: value,
              name: value,
            })) ?? []
          }
          title={filterTitleMap[filterName]}
          selectedValues={filters[filterName] ?? []}
          onChange={values => handleFilterChange(filterName, values)}
        />
      ))}
    </div>
  );
};

/**
 * Returns the job listings page.
 *
 * @param filters - Array of applied filters
 * @param filterValues - Object containing all filter values grouped by keys
 * @param jobs - Jobs to display
 */
export const JobPostings: FC<JobPostingsProps> = ({
  filterValues,
  jobs,
  filters,
  isLoading,
  setQuery,
  filterKey,
  sortJobGroupsAlphabetically,
}) => {
  const handleFilterChange = useCallback(
    (filterId: string, filterValues: string[]) => {
      const params = new URLSearchParams(window.location.search);

      params.delete(filterId);

      for (const value of filterValues) {
        params.append(filterId, value);
      }

      window.history.pushState('', '', `?${params.toString()}`);
      setQuery(params);
    },
    [setQuery]
  );

  const filterNames: JobFilters[] = [
    JobFilterField.LOCATION,
    JobFilterField.ROLE,
    JobFilterField.TYPE,
  ];

  return (
    <article data-testid="mwp-job-postings">
      <section data-testid="jobs-filter-section" className={searchSectionCss}>
        <BlockBoundary className={boundaryCss}>
          <h4 className={cx(searchHeaderCss)}>Jobs at Snap Inc.</h4>
          <div className={cx(filterContainerCss)}>
            <div className={jobFiltersBlockCss}>
              <Filters
                filterValues={filterValues}
                filters={filters}
                handleFilterChange={handleFilterChange}
                filterNames={filterNames}
              />
            </div>
            <div className={jobSearchBarBlockCss}>
              <SearchBar setQuery={setQuery} />
            </div>
          </div>
        </BlockBoundary>
      </section>

      <BlockBoundary className={boundaryCss}>
        <JobsTable
          isLoading={isLoading}
          jobs={jobs}
          filterKey={filterKey}
          sortJobGroupsAlphabetically={sortJobGroupsAlphabetically}
        />
      </BlockBoundary>
    </article>
  );
};
